import { FormEvent, useEffect, useState } from "react";
import { AlertTriangle, CheckCircle2, Circle, CreditCard, Edit, Landmark, Plus, RefreshCw, Save, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import AdminLayout from "./AdminLayout";
import {
  AdminPaymentMethod,
  AdminPaymentMethodPayload,
  AdminUser,
  PAYMENT_GATEWAY_CONFIG_FIELDS,
  PAYMENT_GATEWAY_PROVIDERS,
  PaymentGatewayProvider,
  deleteAdminPaymentMethod,
  getCurrentAdmin,
  listAdminPaymentMethods,
  logout,
  saveAdminPaymentMethod,
} from "./api";
import { useConfirmDialog } from "./components/ConfirmDialog";
import { AdminRadioCardGroup, AdminSwitchCard } from "./components/AdminChoiceControl";

type SettlementType = "Manual" | "Gateway";
type ExtraFeeType = "None" | "Fixed" | "Percentage";

type FormState = {
  Code: string;
  Name: string;
  Description: string;
  Instructions: string;
  SettlementType: SettlementType;
  GatewayProvider: PaymentGatewayProvider | "";
  GatewayConfigValues: Record<string, string>;
  ExtraFeeType: ExtraFeeType;
  ExtraFeeAmount: string;
  PaymentDueDays: string;
  IsActive: boolean;
  SortOrder: string;
};

const emptyForm: FormState = {
  Code: "",
  Name: "",
  Description: "",
  Instructions: "",
  SettlementType: "Manual",
  GatewayProvider: "",
  GatewayConfigValues: {},
  ExtraFeeType: "None",
  ExtraFeeAmount: "0",
  PaymentDueDays: "",
  IsActive: true,
  SortOrder: "100",
};

function itemToForm(item: AdminPaymentMethod): FormState {
  return {
    Code: item.Code,
    Name: item.Name,
    Description: item.Description || "",
    Instructions: item.Instructions || "",
    SettlementType: item.SettlementType,
    GatewayProvider: item.GatewayProvider || "",
    // 金鑰內容後端不會回傳明碼，欄位一律留空（留空 = 送出時保留原本已設定的值，見 api.ts 的合併寫入說明）。
    GatewayConfigValues: {},
    ExtraFeeType: item.ExtraFeeType,
    ExtraFeeAmount: item.ExtraFeeType === "Percentage" ? String(item.ExtraFeeAmount / 100) : String(item.ExtraFeeAmount),
    PaymentDueDays: item.PaymentDueDays !== null ? String(item.PaymentDueDays) : "",
    IsActive: item.IsActive,
    SortOrder: String(item.SortOrder),
  };
}

const inputClass = "h-11 w-full rounded-md border border-[#cfd8d2] px-3 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15";

const extraFeeTypeLabels: Record<ExtraFeeType, string> = { None: "不收手續費", Fixed: "固定金額", Percentage: "依比例計算" };

export default function AdminPaymentMethods() {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState<AdminUser | null>(null);
  const [items, setItems] = useState<AdminPaymentMethod[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageMessage, setPageMessage] = useState("");
  const [editingItem, setEditingItem] = useState<AdminPaymentMethod | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);
  const [formOpen, setFormOpen] = useState(false);
  const [formMessage, setFormMessage] = useState("");
  const [saving, setSaving] = useState(false);
  const { confirm, ConfirmDialog } = useConfirmDialog();

  async function load() {
    setLoading(true);
    setPageMessage("");
    try {
      const me = await getCurrentAdmin();
      setCurrentUser(me.User);
      const data = await listAdminPaymentMethods();
      setItems(data.Items);
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "付款方式資料載入失敗。");
      navigate("/admin-login", { replace: true });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function handleLogout() {
    await logout().catch(() => undefined);
    navigate("/admin-login", { replace: true });
  }

  function openCreateForm() {
    setEditingItem(null);
    setForm(emptyForm);
    setFormMessage("");
    setFormOpen(true);
  }

  function openEditForm(item: AdminPaymentMethod) {
    setEditingItem(item);
    setForm(itemToForm(item));
    setFormMessage("");
    setFormOpen(true);
  }

  function closeForm() {
    if (saving) return;
    setFormOpen(false);
    setEditingItem(null);
    setForm(emptyForm);
  }

  function setGatewayConfigValue(key: string, value: string) {
    setForm((current) => ({ ...current, GatewayConfigValues: { ...current.GatewayConfigValues, [key]: value } }));
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!form.Code.trim()) { setFormMessage("請輸入代碼。"); return; }
    if (!form.Name.trim()) { setFormMessage("請輸入付款方式名稱。"); return; }
    if (form.SettlementType === "Gateway" && !form.GatewayProvider) { setFormMessage("請選擇金流商。"); return; }

    const extraFeeAmount =
      form.ExtraFeeType === "Percentage"
        ? Math.round(Number(form.ExtraFeeAmount || 0) * 100)
        : form.ExtraFeeType === "Fixed"
          ? Math.round(Number(form.ExtraFeeAmount || 0))
          : 0;

    const gatewayConfig: Record<string, string> = {};
    if (form.SettlementType === "Gateway") {
      Object.entries(form.GatewayConfigValues).forEach(([key, value]) => {
        if (value.trim() !== "") { gatewayConfig[key] = value.trim(); }
      });
    }

    const payload: AdminPaymentMethodPayload = {
      Code: form.Code.trim(),
      Name: form.Name.trim(),
      Description: form.Description.trim() || undefined,
      Instructions: form.Instructions.trim() || undefined,
      SettlementType: form.SettlementType,
      GatewayProvider: form.SettlementType === "Gateway" ? (form.GatewayProvider as PaymentGatewayProvider) : "",
      GatewayConfig: gatewayConfig,
      ExtraFeeType: form.ExtraFeeType,
      ExtraFeeAmount: extraFeeAmount,
      PaymentDueDays: form.SettlementType === "Manual" && form.PaymentDueDays.trim() !== "" ? Number(form.PaymentDueDays) : null,
      IsActive: form.IsActive,
      SortOrder: Number(form.SortOrder || 100),
    };

    setSaving(true);
    setFormMessage("");
    try {
      await saveAdminPaymentMethod(editingItem?.PaymentMethodId || null, payload);
      closeForm();
      await load();
    } catch (error) {
      setFormMessage(error instanceof Error ? error.message : "付款方式儲存失敗。");
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: AdminPaymentMethod) {
    const confirmed = await confirm({
      title: "刪除這個付款方式？",
      description: (
        <>
          確定要刪除 <strong className="font-bold text-[#203d32]">{item.Name}</strong> 嗎？
          <br />
          刪除後結帳頁不會再顯示此選項，但不影響已存在的歷史訂單顯示。
        </>
      ),
      confirmText: "刪除付款方式",
      cancelText: "取消",
      tone: "danger",
    });
    if (!confirmed) return;

    try {
      await deleteAdminPaymentMethod(item.PaymentMethodId);
      await load();
    } catch (error) {
      setPageMessage(error instanceof Error ? error.message : "付款方式刪除失敗。");
    }
  }

  const activeGatewayFields = form.GatewayProvider ? PAYMENT_GATEWAY_CONFIG_FIELDS[form.GatewayProvider] : [];
  const editingGatewayStatus = editingItem?.GatewayConfigStatus || {};

  return (
    <AdminLayout sectionLabel="網站設定" title="付款方式設定" user={currentUser} onLogout={handleLogout}>
      <p className="mb-4 text-xs text-[#7b877f]">
        結帳頁提供給客人選擇的付款方式。「人工結算」類型（例如貨到付款、銀行轉帳）由客服人工確認收款後於訂單管理更新付款狀態，可設定付款期限天數，
        逾期未付款會自動取消訂單並歸還庫存；「線上金流」類型需選擇金流商並填入商店代號／金鑰，尚未完成設定前即使上架也不會出現在結帳頁供客人選擇，
        待日後取得正式金鑰後再回來補上設定即可啟用，不用等到金鑰到位才能建立這個付款方式。
      </p>

      <div className="mb-4 flex justify-end gap-2">
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-5 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => load()} type="button">
          <RefreshCw size={18} />
          重新整理
        </button>
        <button className="inline-flex h-11 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32]" onClick={openCreateForm} type="button">
          <Plus size={20} />
          新增付款方式
        </button>
      </div>

      {pageMessage && <div className="mb-4 rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{pageMessage}</div>}

      <div className="rounded-md border border-[#dfe6e1] bg-white">
        <div className="overflow-auto">
          <table className="w-full min-w-[920px] table-fixed border-collapse text-left">
            <thead className="bg-[#f0f3f1] text-sm text-[#59665f] shadow-[inset_0_-1px_0_#dfe6e1]">
              <tr>
                <th className="w-[220px] px-4 py-3">付款方式</th>
                <th className="w-[150px] px-4 py-3">類型</th>
                <th className="w-[140px] px-4 py-3">手續費</th>
                <th className="w-[120px] px-4 py-3">付款期限</th>
                <th className="w-[130px] px-4 py-3">狀態</th>
                <th className="w-[80px] px-4 py-3">排序</th>
                <th className="w-[160px] px-4 py-3">操作</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>載入中...</td></tr>
              ) : items.length === 0 ? (
                <tr><td className="px-4 py-10 text-center text-[#59665f]" colSpan={7}>尚未設定任何付款方式。</td></tr>
              ) : items.map((item) => (
                <tr className="border-t border-[#edf0ee]" key={item.PaymentMethodId}>
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-2">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#e9f4ec] text-[#245438]">
                        {item.SettlementType === "Gateway" ? <CreditCard size={16} /> : <Landmark size={16} />}
                      </span>
                      <div className="min-w-0">
                        <p className="truncate font-bold text-[#203d32]">{item.Name}</p>
                        <p className="text-xs text-[#9a8a7a]">{item.Code}</p>
                      </div>
                    </div>
                    {item.Description && <p className="mt-1 text-xs text-[#7b877f]">{item.Description}</p>}
                  </td>
                  <td className="px-4 py-4">
                    {item.SettlementType === "Gateway" ? (
                      <div>
                        <span className="inline-flex rounded-full bg-[#eef2ff] px-3 py-1 text-xs font-bold text-[#3a3f9c]">線上金流・{item.GatewayProvider}</span>
                        {!item.IsGatewayReady && (
                          <p className="mt-1 flex items-center gap-1 text-xs font-bold text-[#b3261e]">
                            <AlertTriangle size={12} />
                            金鑰尚未設定完成
                          </p>
                        )}
                      </div>
                    ) : (
                      <span className="inline-flex rounded-full bg-[#f0f3f1] px-3 py-1 text-xs font-bold text-[#59665f]">人工結算</span>
                    )}
                  </td>
                  <td className="px-4 py-4">
                    {item.ExtraFeeType === "None"
                      ? "免手續費"
                      : item.ExtraFeeType === "Fixed"
                        ? `NT$ ${item.ExtraFeeAmount.toLocaleString()}`
                        : `${(item.ExtraFeeAmount / 100).toLocaleString()}%`}
                  </td>
                  <td className="px-4 py-4">{item.SettlementType === "Manual" ? (item.PaymentDueDays !== null ? `${item.PaymentDueDays} 天內` : "不限期限") : "-"}</td>
                  <td className="px-4 py-4">
                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-bold ${item.IsActive ? "bg-[#e9f4ec] text-[#245438]" : "bg-[#eeeeee] text-[#59635d]"}`}>{item.IsActive ? "上架" : "下架"}</span>
                  </td>
                  <td className="px-4 py-4">{item.SortOrder}</td>
                  <td className="px-4 py-4">
                    <div className="grid gap-2">
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#cfd8d2] bg-white px-3 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={() => openEditForm(item)} type="button">
                        <Edit size={16} />
                        編輯
                      </button>
                      <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md border border-[#f2b8b5] bg-white px-3 font-bold text-[#b3261e] transition hover:bg-[#fdebea]" onClick={() => handleDelete(item)} type="button">
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {formOpen && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-[#10251c]/40 px-0 py-0 sm:items-center sm:px-4 sm:py-6">
          <button aria-label="關閉視窗" className="absolute inset-0 cursor-default" onClick={closeForm} type="button" />
          <form className="relative flex max-h-[90vh] w-full max-w-[760px] flex-col overflow-hidden rounded-t-lg border border-[#dfe6e1] bg-white shadow-[0_22px_70px_rgba(32,61,50,.2)] sm:rounded-md" onSubmit={handleSubmit}>
            <div className="flex shrink-0 items-start justify-between gap-4 border-b border-[#edf0ee] px-5 py-4">
              <div>
                <h2 className="text-xl font-bold text-[#203d32]">{editingItem ? "編輯付款方式" : "新增付款方式"}</h2>
                <p className="mt-1 text-sm text-[#7b877f]">會顯示於前台結帳頁供客人選擇。</p>
              </div>
              <button className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-[#d8e0db] text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                <X size={18} />
              </button>
            </div>

            <div className="grid gap-4 overflow-y-auto px-5 py-5">
              {formMessage && <div className="rounded-md border border-[#f2b8b5] bg-[#fdebea] px-4 py-3 text-sm font-medium text-[#8a1f1f]">{formMessage}</div>}

              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>代碼 <span className="text-[#c3423f]">*</span></span>
                  <input className={inputClass} placeholder="例如 COD、CREDIT_CARD" value={form.Code} onChange={(event) => setForm((current) => ({ ...current, Code: event.target.value.toUpperCase() }))} />
                </label>
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>名稱 <span className="text-[#c3423f]">*</span></span>
                  <input className={inputClass} placeholder="例如 貨到付款" value={form.Name} onChange={(event) => setForm((current) => ({ ...current, Name: event.target.value }))} />
                </label>
              </div>
              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                <span>說明（選填）</span>
                <input className={inputClass} placeholder="顯示在結帳頁的補充說明" value={form.Description} onChange={(event) => setForm((current) => ({ ...current, Description: event.target.value }))} />
              </label>

              <div className="grid gap-2">
                <span className="text-sm font-bold text-[#315746]">結算方式</span>
                <AdminRadioCardGroup<SettlementType>
                  name="SettlementType"
                  value={form.SettlementType}
                  onChange={(SettlementType) => setForm((current) => ({ ...current, SettlementType }))}
                  options={[
                    { value: "Manual", label: "人工結算", description: "例如貨到付款、銀行轉帳，由客服人工確認收款後更新狀態。", icon: <Landmark size={16} /> },
                    { value: "Gateway", label: "線上金流", description: "串接金流商，客人線上付款，系統自動收到通知更新狀態。", icon: <CreditCard size={16} /> },
                  ]}
                />
              </div>

              {form.SettlementType === "Manual" ? (
                <>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>付款期限天數（選填）</span>
                    <input className={inputClass} type="number" min={0} placeholder="留空表示不限期限" value={form.PaymentDueDays} onChange={(event) => setForm((current) => ({ ...current, PaymentDueDays: event.target.value }))} />
                    <span className="text-xs font-normal text-[#7b877f]">超過期限仍未付款的訂單，系統會自動取消並歸還庫存（例如銀行轉帳可設 3 天）。</span>
                  </label>
                  <label className="grid gap-2 text-sm font-bold text-[#315746]">
                    <span>付款說明（選填，會顯示給客人）</span>
                    <textarea
                      className="min-h-[110px] rounded-md border border-[#cfd8d2] px-3 py-2 font-normal outline-none focus:border-[#315746] focus:ring-2 focus:ring-[#315746]/15"
                      placeholder={"例如銀行轉帳：\n銀行：OO銀行（代碼 013）\n戶名：富聆聽健康有限公司\n帳號：1234-5678-9012\n\n完成匯款後請來電或私訊告知帳號後五碼，以利對帳出貨。"}
                      value={form.Instructions}
                      onChange={(event) => setForm((current) => ({ ...current, Instructions: event.target.value }))}
                    />
                    <span className="text-xs font-normal text-[#7b877f]">
                      客人使用此付款方式下單後，只要訂單還在「待付款」狀態，就會在訂單完成頁、查詢訂單頁與訂單確認信裡看到這段文字（支援換行）。留空則只會顯示系統預設的通用提示文字。
                    </span>
                  </label>
                </>
              ) : (
                <div className="grid gap-4 rounded-md border border-[#e3ded0] bg-[#fbfaf7] p-4">
                  <div className="grid gap-2">
                    <span className="text-sm font-bold text-[#315746]">金流商 <span className="text-[#c3423f]">*</span></span>
                    <AdminRadioCardGroup<PaymentGatewayProvider | "">
                      name="GatewayProvider"
                      value={form.GatewayProvider}
                      onChange={(GatewayProvider) => setForm((current) => ({ ...current, GatewayProvider }))}
                      options={PAYMENT_GATEWAY_PROVIDERS.map((provider) => ({ value: provider, label: provider }))}
                    />
                  </div>

                  {form.GatewayProvider && (
                    <div className="grid gap-3">
                      <p className="text-xs text-[#7b877f]">
                        金鑰欄位留空表示「保留原本已設定的值」，不會清空既有設定；正式上線前請務必先在金流商的測試環境完整跑過一次付款流程再切換正式金鑰。
                      </p>
                      {activeGatewayFields.map((field) => {
                        const isSet = !!editingGatewayStatus[field.key];
                        return (
                          <label className="grid gap-2 text-sm font-bold text-[#315746]" key={field.key}>
                            <span className="flex items-center gap-1.5">
                              {editingItem ? (
                                isSet ? <CheckCircle2 className="text-[#2f8f5b]" size={14} /> : <Circle className="text-[#c3cdc7]" size={14} />
                              ) : null}
                              {field.label}
                            </span>
                            <input
                              className={inputClass}
                              placeholder={editingItem ? (isSet ? "已設定，留空維持原值" : "尚未設定") : field.placeholder}
                              value={form.GatewayConfigValues[field.key] || ""}
                              onChange={(event) => setGatewayConfigValue(field.key, event.target.value)}
                            />
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              <div className="grid gap-2">
                <span className="text-sm font-bold text-[#315746]">手續費</span>
                <AdminRadioCardGroup<ExtraFeeType>
                  name="ExtraFeeType"
                  value={form.ExtraFeeType}
                  onChange={(ExtraFeeType) => setForm((current) => ({ ...current, ExtraFeeType }))}
                  options={(["None", "Fixed", "Percentage"] as ExtraFeeType[]).map((type) => ({ value: type, label: extraFeeTypeLabels[type] }))}
                  columnsClassName="sm:grid-cols-3"
                />
              </div>
              {form.ExtraFeeType !== "None" && (
                <label className="grid gap-2 text-sm font-bold text-[#315746]">
                  <span>手續費{form.ExtraFeeType === "Percentage" ? "比例（%）" : "金額（元）"}</span>
                  <input className={inputClass} type="number" min={0} step={form.ExtraFeeType === "Percentage" ? "0.01" : "1"} value={form.ExtraFeeAmount} onChange={(event) => setForm((current) => ({ ...current, ExtraFeeAmount: event.target.value }))} />
                  <span className="text-xs font-normal text-[#7b877f]">依「商品小計＋運費」計算，計入訂單總金額。</span>
                </label>
              )}

              <label className="grid gap-2 text-sm font-bold text-[#315746]">
                排序（數字越小越前面）
                <input className={inputClass} type="number" value={form.SortOrder} onChange={(event) => setForm((current) => ({ ...current, SortOrder: event.target.value }))} />
              </label>
              <AdminSwitchCard checked={form.IsActive} description="下架後結帳頁不會再顯示此付款方式。" label="上架顯示" onChange={(IsActive) => setForm((current) => ({ ...current, IsActive }))} />
            </div>

            <div className="flex shrink-0 flex-col-reverse gap-3 border-t border-[#edf0ee] px-5 py-4 sm:flex-row sm:justify-end">
              <button className="h-10 rounded-md border border-[#cfd8d2] bg-white px-4 font-bold text-[#315746] transition hover:bg-[#f0f5f2]" onClick={closeForm} type="button">
                取消
              </button>
              <button className="inline-flex h-10 items-center justify-center gap-2 rounded-md bg-[#315746] px-5 font-bold text-white transition hover:bg-[#203d32] disabled:cursor-not-allowed disabled:opacity-60" disabled={saving} type="submit">
                <Save size={18} />
                {saving ? "儲存中" : "儲存付款方式"}
              </button>
            </div>
          </form>
        </div>
      )}

      <ConfirmDialog />
    </AdminLayout>
  );
}
